import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { logError } from '@cortex/faro';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Card, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { useBdDocument } from '../../../hooks/useBasicDesign.js';
import { columnOf, labelOf } from './bd-defs.js';
import { toneOf } from './bd-presentation.js';
import { bdChangeList, bdDecisionMatrix, bdIntegrationMatrix, matrixCell } from './bd-views.js';
import { BdDiagrams } from './BdDiagrams.js';
import type {
  BdArtifact,
  BdArtifactDef,
  BdRow,
  BdSavedArtifact,
} from '../../../lib/api/basic-design.js';
import type { BdViewSettings } from '../../../lib/bd-view-settings.js';

export interface BdOverviewProps {
  /** 基本設計のチケット */
  bdKey: string;
  /** 全節の定義 */
  defs: readonly BdArtifactDef[];
  /** 保存済みの節 */
  saved: Partial<Record<BdArtifact, BdSavedArtifact>>;
  /** 個人の見え方（図の列と向き） */
  settings: BdViewSettings;
  /** 個人の見え方を変える */
  onSettingsChange: (change: (current: BdViewSettings) => BdViewSettings) => void;
  /** 節を開く（図の札から） */
  onOpenSection: (artifact: BdArtifact) => void;
}

/** 表の見出しのセル。 @graph-connects none */
const TH_CLASS =
  'whitespace-nowrap border-b border-[var(--color-border)] px-3 py-2 text-left text-xs font-medium text-[var(--color-text-muted)]';

/** 表の本文のセル。 @graph-connects none */
const TD_CLASS = 'border-b border-[var(--color-border)] px-3 py-2 align-top';

/** 見出しつきの枠。 @graph-connects none */
function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section aria-label={title}>
      <Card padding="sm">
        <CardContent className="mt-0 space-y-3">
          <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">{title}</h3>
          {children}
        </CardContent>
      </Card>
    </section>
  );
}

/** 何も無いときの一文。 @graph-connects none */
function Empty({ text }: { text: string }) {
  return <p className="text-sm text-[var(--color-text-muted)]">{text}</p>;
}

/**
 * 基本設計の全体。全体図・ER 図・画面遷移図（API が行から組み立てた動く図）、変更一覧、サービスの連携
 * マトリクス、ケース × 状態の判定マトリクス、Markdown の書き出し。
 *
 * 変更一覧とマトリクスは別に保存せず、画面・エンティティ・API・実現方針・判定ロジックの行から
 * 毎回組み立てる（書き出しと同じ組み立て方）。書き出しは押したときの保存内容で作る。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計の全体図・ER 図・画面遷移図・変更一覧・連携マトリクス・判定マトリクスを読み、Markdown に書き出してコピーする
 * @graph-connects BdDiagrams [calls] 全体図・ER 図・画面遷移図
 * @graph-connects useBdDocument [calls] Markdown の書き出し
 * @graph-connects bdChangeList [calls] 変更一覧の組み立て
 * @graph-connects bdIntegrationMatrix [calls] 連携マトリクスの組み立て
 * @graph-connects bdDecisionMatrix [calls] 判定マトリクスの組み立て
 */
export function BdOverview({
  bdKey,
  defs,
  saved,
  settings,
  onSettingsChange,
  onOpenSection,
}: BdOverviewProps) {
  const { t } = useTranslation();
  const rowsOf = (artifact: BdArtifact): BdRow[] => saved[artifact]?.rows ?? [];
  const defOf = (artifact: BdArtifact): BdArtifactDef | undefined =>
    defs.find((d) => d.key === artifact);

  const changes = bdChangeList(rowsOf);
  const integration = bdIntegrationMatrix(rowsOf('approaches'));
  const decision = bdDecisionMatrix(rowsOf('decision_logic'));
  const approachesDef = defOf('approaches');
  const mechanismColumn =
    approachesDef === undefined ? undefined : columnOf(approachesDef, 'mechanism');

  return (
    <section className="space-y-4" aria-label={t('bd.groups.overview')}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h2 className="text-base font-semibold text-[var(--color-text-primary)]">
          {t('bd.groups.overview')}
        </h2>
        <CopyMarkdownButton bdKey={bdKey} />
      </div>

      <Block title={t('bd.overview.diagram')}>
        <BdDiagrams
          bdKey={bdKey}
          defs={defs}
          saved={saved}
          settings={settings}
          onSettingsChange={onSettingsChange}
          onOpenSection={onOpenSection}
        />
      </Block>

      <Block title={t('bd.overview.changeList')}>
        {changes.length === 0 ? (
          <Empty text={t('bd.overview.changeEmpty')} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr>
                  <th className={TH_CLASS}>{t('bd.overview.kind')}</th>
                  <th className={TH_CLASS}>ID</th>
                  <th className={TH_CLASS}>{t('bd.overview.name')}</th>
                  <th className={TH_CLASS}>{t('bd.overview.impact')}</th>
                  <th className={TH_CLASS}>{t('bd.overview.decisions')}</th>
                </tr>
              </thead>
              <tbody>
                {changes.map((change, i) => {
                  const def = defOf(change.artifact);
                  const impactColumn = def === undefined ? undefined : columnOf(def, 'impact');
                  return (
                    // 同じ ID の行が節をまたいで出ることがあるので、位置で鍵を作る
                    <tr key={`${String(i)}:${change.artifact}:${change.id}`}>
                      <td className={TD_CLASS}>{t(`bd.overview.kinds.${change.kind}`)}</td>
                      <td className={`${TD_CLASS} font-mono text-xs`}>{change.id}</td>
                      <td className={`${TD_CLASS} break-all`}>{change.name}</td>
                      <td className={TD_CLASS}>
                        <Badge size="sm" variant={toneOf('impact', change.impact)}>
                          {labelOf(impactColumn, change.impact)}
                        </Badge>
                      </td>
                      <td className={`${TD_CLASS} font-mono text-xs`}>
                        {change.decisions.join(', ')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Block>

      <Block title={t('bd.overview.integration')}>
        {integration.cells.length === 0 ? (
          <Empty text={t('bd.overview.integrationEmpty')} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr>
                  <th className={TH_CLASS}>{t('bd.overview.fromTo')}</th>
                  {integration.callees.map((to) => (
                    <th key={to} className={TH_CLASS}>
                      {to}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {integration.callers.map((from) => (
                  <tr key={from}>
                    <th scope="row" className={`${TD_CLASS} text-left font-medium`}>
                      {from}
                    </th>
                    {integration.callees.map((to) => {
                      const found = integration.cells.find((c) => c.from === from && c.to === to);
                      return (
                        <td key={to} className={TD_CLASS}>
                          {found === undefined ? null : (
                            <>
                              <span className="block">
                                {found.mechanisms
                                  .map((m) => labelOf(mechanismColumn, m))
                                  .join(', ')}
                              </span>
                              <span className="block font-mono text-xs text-[var(--color-text-muted)]">
                                {found.ids.join(', ')}
                              </span>
                            </>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Block>

      <Block title={t('bd.overview.decision')}>
        {decision.cells.length === 0 ? (
          <Empty text={t('bd.overview.decisionEmpty')} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr>
                  <th className={TH_CLASS}>{t('bd.overview.caseState')}</th>
                  {decision.states.map((state) => (
                    <th key={state} className={TH_CLASS}>
                      {state}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {decision.cases.map((caseLabel) => (
                  <tr key={caseLabel}>
                    <th scope="row" className={`${TD_CLASS} text-left font-medium`}>
                      {caseLabel}
                    </th>
                    {decision.states.map((state) => {
                      const found = matrixCell(decision.cells, caseLabel, state);
                      return (
                        <td key={state} className={TD_CLASS}>
                          {found === undefined ? (
                            <span className="text-xs text-[var(--color-warning-text)]">
                              {t('bd.overview.gap')}
                            </span>
                          ) : (
                            <ul className="space-y-0.5">
                              {found.outcomes.map((outcome, i) => (
                                // 同じ結果の行が並ぶことがあるので、位置で鍵を作る
                                <li key={`${String(i)}:${found.ids[i] ?? ''}`}>
                                  <span className="mr-1 font-mono text-xs text-[var(--color-text-muted)]">
                                    {found.ids[i]}
                                  </span>
                                  {outcome}
                                </li>
                              ))}
                            </ul>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Block>
    </section>
  );
}

/** コピーの結果。 @graph-connects none */
type CopyState = 'idle' | 'copied' | 'failed';

/**
 * 「Markdown でコピー」。押したときに API で書き出し、クリップボードへ入れる。
 *
 * クリップボードは権限やフォーカスで書けないことがある（ブラウザが拒む）。その失敗は
 * 画面に出すだけでなく記録に残す。
 * @graph-connects useBdDocument [calls] Markdown の書き出し
 * @graph-connects logError [calls] コピーの失敗の記録
 */
function CopyMarkdownButton({ bdKey }: { bdKey: string }) {
  const { t } = useTranslation();
  const exportDocument = useBdDocument(bdKey);
  const [state, setState] = useState<CopyState>('idle');

  return (
    <div className="flex items-center gap-2">
      {state === 'idle' ? null : (
        <span
          role="status"
          className={`text-xs ${state === 'copied' ? 'text-[var(--color-success-text)]' : 'text-[var(--color-error-text)]'}`}
        >
          {state === 'copied' ? t('bd.overview.copied') : t('bd.overview.copyFailed')}
        </span>
      )}
      <Button
        size="sm"
        variant="secondary"
        loading={exportDocument.isPending}
        disabled={exportDocument.isPending}
        onClick={() => {
          setState('idle');
          exportDocument.mutate(undefined, {
            onSuccess: ({ markdown }) => {
              navigator.clipboard.writeText(markdown).then(
                () => {
                  setState('copied');
                },
                (error: unknown) => {
                  logError(error instanceof Error ? error : new Error(String(error)), {
                    event: 'bd_document_copy_failed',
                  });
                  setState('failed');
                },
              );
            },
            onError: () => {
              setState('failed');
            },
          });
        }}
      >
        {exportDocument.isPending ? t('bd.overview.copying') : t('bd.overview.copy')}
      </Button>
    </div>
  );
}
